import type { CellHistoryItem } from '@tabel/shared/schemas/cells';
import type { RecalcHistoryEntry } from '@tabel/shared/schemas/rules';
import { History } from 'lucide-react';
import { type HoursFormat, hoursText, ruDate, ruDateTime } from '../lib/format';
import { typeLabel } from '../lib/questions';
import { sayRecalc, sideText } from '../lib/recalc';

/** Действия журнала по дню словами. Нового типа нет — код типа. */
const ACTION_LABEL: Readonly<Record<string, string>> = {
  'cell.edit': 'Правка',
  'cell.edit_cancel': 'Отмена правки',
  'cell.lunch_set': 'Отмечено «в обед»',
  'cell.lunch_clear': 'Снято «в обед»',
  'cell.absence_set': 'Код отсутствия',
  'cell.absence_clear': 'Код отсутствия снят',
  'row.add': 'Добавление в табель',
  'row.remove': 'Удаление из табеля',
};

/** Почему система сняла вопрос (`question_states.removed_reason`). */
const REMOVED_REASON: Readonly<Record<string, string>> = {
  flag_gone: 'пометка исчезла после пересчёта',
  absence: 'на день есть документ',
  row_removed: 'человека убрали из табеля',
  not_eligible: 'день больше не подходит для вопроса',
};

/** Решение кадров словами. */
const DECISION: Readonly<Record<string, string>> = { accepted: 'принято', clarify: 'уточнить' };

/** Состояние вопроса после «Отменить» (`basis.restored.state`), кроме ответа — его показывает сам ответ. */
const RESTORED_STATE: Readonly<Record<string, string>> = {
  open: 'вопрос снова без ответа',
  later: 'вопрос снова отложен: «Уточню позже»',
  clarify: 'вопрос снова «Уточнить»',
};

type Rec = Record<string, unknown>;
const rec = (v: unknown): Rec => (v !== null && typeof v === 'object' ? (v as Rec) : {});
const str = (v: unknown): string | null => (typeof v === 'string' ? v : null);
const num = (v: unknown): number | null => (typeof v === 'number' ? v : null);

interface Ctx {
  /** День карточки (`YYYY-MM-DD`): время бригадира другой даты — с датой. */
  date: string;
  tzOffset: string;
  format: HoursFormat | null;
}

const hoursOf = (h: number, c: Ctx) => `${hoursText(h, c.format)} ч`;

/** Тип вопроса словами в кавычках: «Нет выхода». */
const quoted = (type: unknown) => `«${typeLabel(str(type) ?? 'вопрос')}»`;

/**
 * Ответ словами по полям записи (`option`, `hours`, `statedKind`, `statedAt`): «выход в 19:00 · указано бригадиром →
 * 10 ч» (время другой даты — «выход 06.10 в 02:30»), «9 ч», «работы не было → 0 ч», «часы верны». Нет ответа — пусто.
 */
function answerText(a: Rec, c: Ctx): string {
  const hours = num(a.hours);
  const h = hours === null ? '' : hoursOf(hours, c);
  switch (a.option) {
    case 'time': {
      const at = str(a.statedAt);
      const local = at ? ruDateTime(at, c.tzOffset) : '';
      const day = local && local.slice(0, 10) !== ruDate(c.date) ? ` ${local.slice(0, 5)}` : '';
      const kind = a.statedKind === 'вход' ? 'вход' : 'выход';
      return `${kind}${day} в ${local.slice(11)} · указано бригадиром${h ? ` → ${h}` : ''}`;
    }
    case 'zero':
      return 'работы не было → 0 ч';
    case 'accept':
      return h ? `часы верны → ${h}` : 'часы верны';
    case 'later':
      return 'уточню позже';
    default:
      return h;
  }
}

/** Что отменено «Отменить» и что вернулось (`action.undo`, `basis`: `type`, `questionType`, `restored`). */
function undoText(b: Rec, c: Ctx): string {
  const type = str(b.type) ?? '';
  const restored = rec(b.restored);
  const question = quoted(b.questionType);
  if (type === 'question.answer' || type === 'question.later') {
    const what =
      type === 'question.later' ? `«Уточню позже» по вопросу ${question}` : `ответ на вопрос ${question}`;
    const state = str(restored.state) ?? '';
    const back = RESTORED_STATE[state] ?? `вернулся ответ: ${answerText(restored, c)}`;
    return `Отменено: ${what} — ${back}`;
  }
  if (type === 'cell.edit' || type === 'cell.edit_cancel') {
    const hours = num(restored.manualHours);
    const back = hours === null ? 'правки нет, часы по проходам' : `вернулись ${hoursOf(hours, c)}`;
    return `Отменено: ${type === 'cell.edit' ? 'правка' : 'отмена правки'} — ${back}`;
  }
  if (type === 'row.add' || type === 'row.remove') {
    const back = restored.inTimesheet ? 'человек снова в табеле' : 'человека нет в табеле';
    return `Отменено: ${type === 'row.add' ? 'добавление в табель' : 'удаление из табеля'} — ${back}`;
  }
  const label = ACTION_LABEL[type] ?? type;
  return `Отменено: ${label.charAt(0).toLowerCase()}${label.slice(1)}`;
}

/** Действие записи со значением словами. */
function whatText(h: CellHistoryItem, c: Ctx): string {
  const after = rec(h.after);
  const basis = rec(h.basis);
  switch (h.type) {
    case 'question.answer': {
      // Отмена правки по ячейке вернула вопрос в «без ответа» — своя подпись (T101).
      if (after.reopened || (basis.via === 'cell.edit_cancel' && after.state === 'open')) {
        return `Вопрос ${quoted(after.type)} снова без ответа: правка отменена`;
      }
      const viaEdit = basis.via === 'cell.edit' ? ' правкой часов' : '';
      const value = answerText(after, c);
      const tail = [
        after.changedAfterAccept ? 'изменён после принятия' : null,
        basis.batchId !== undefined ? 'ответ сразу нескольким' : null,
      ].filter(Boolean);
      return `Ответ на вопрос ${quoted(after.type)}${viaEdit}${value ? `: ${value}` : ''}${tail.map((t) => ` · ${t}`).join('')}`;
    }
    case 'question.later':
      return `Вопрос ${quoted(after.type)} отложен: «Уточню позже»`;
    case 'question.review': {
      const decision = DECISION[str(after.state) ?? ''];
      return `Решение кадров по вопросу ${quoted(after.type)}${decision ? `: ${decision}` : ''}`;
    }
    case 'question.removed': {
      const reason = REMOVED_REASON[str(after.reason) ?? ''];
      return `Вопрос ${quoted(after.type)} снят${reason ? `: ${reason}` : ''}`;
    }
    case 'action.undo':
      return undoText(basis, c);
    case 'cell.edit': {
      const hours = num(after.manualHours);
      return hours === null ? 'Правка' : `Правка ${hoursOf(hours, c)}`;
    }
    case 'cell.absence_set':
      return `Код отсутствия ${str(after.absenceCode) ?? ''}`.trim();
    case 'edit.decision': {
      // «Правка устарела» (004 FR-044): кадры оставили правку или приняли новый расчёт.
      if (after.decision !== 'accept') return 'Правка оставлена после пересчёта';
      const hours = num(after.machineHours);
      return hours === null
        ? 'Принят расчёт: правка снята'
        : `Принят расчёт ${hoursOf(hours, c)}: правка снята`;
    }
    default:
      return ACTION_LABEL[h.type] ?? h.type;
  }
}

/** Одна запись строкой: «время · роль, ФИО · действие значение — комментарий · табель «…»». */
function line(h: CellHistoryItem, timesheetId: number, c: Ctx): string {
  // Запись системы (снятие вопроса пересчётом) — без роли и автора.
  const who =
    h.role === null || (h.type === 'question.removed' && !h.author)
      ? 'Система'
      : [h.role === 'hr' ? 'Кадры' : 'Бригадир', h.author].filter(Boolean).join(', ');
  return [
    `${ruDateTime(h.ts, c.tzOffset)} · ${who} · ${whatText(h, c)}`,
    h.comment ? ` — ${h.comment}` : '',
    h.timesheetId === timesheetId ? '' : ` · табель «${h.timesheetName}»`,
  ].join('');
}

/** Пересчёт дня по новым правилам (004, FR-042) строкой: «время · Система · Пересчитано по правилам с …». */
function recalcLine(r: RecalcHistoryEntry, c: Ctx): string {
  const what = sayRecalc.historyLine(r.validFrom, r.basis.kind, sideText(r.before), sideText(r.after));
  return `${ruDateTime(r.at, c.tzOffset)} · Система · ${what}`;
}

/**
 * «История изменений» дня (FR-004): записи журнала о дне человека из всех его табелей на эту дату, по времени.
 * В записи — время, роль и ФИО автора, действие, значение, комментарий; у записи из другого табеля — его название
 * (код отсутствия оттуда — документ дня и здесь). Вопросы (T101): тип вопроса, ответ словами — время бригадира с
 * пометкой «указано бригадиром», «изменён после принятия», ответ правкой часов, ответ сразу нескольким, повторное
 * открытие отменой правки, решения кадров, снятие системой; «Отменить» — что отменено и что вернулось. Пересчёт по
 * новым правилам (004) — запись системы «Пересчитано по правилам с …: было … → стало …».
 */
export function HistoryList({
  items,
  recalcs = [],
  date,
  timesheetId,
  tzOffset,
  format = null,
}: {
  items: readonly CellHistoryItem[];
  /** Пересчёты дня после публикации и возврата правил (`CalcResponse.recalcs`): записи системы. */
  recalcs?: readonly RecalcHistoryEntry[];
  /** День карточки (`YYYY-MM-DD`): время бригадира другой даты показывается с датой. */
  date: string;
  /** Открытый табель: его записи — без названия табеля. */
  timesheetId: number;
  tzOffset: string;
  /** Формат часов правил объекта; `null` — часы 001. */
  format?: HoursFormat | null;
}) {
  const ctx: Ctx = { date, tzOffset, format };
  // Записи журнала и пересчёты — одним списком по времени.
  const sorted = [
    ...items.map((h, i) => ({
      at: h.ts,
      key: `${h.timesheetId}-${h.ts}-${h.type}-${i}`,
      text: line(h, timesheetId, ctx),
    })),
    ...recalcs.map((r, i) => ({
      at: r.at,
      key: `recalc-${r.versionId}-${r.at}-${i}`,
      text: recalcLine(r, ctx),
    })),
  ].sort((a, b) => Date.parse(a.at) - Date.parse(b.at));
  return (
    <section aria-label="История изменений">
      <h3 className="flex items-center gap-1.5 text-[14px] font-semibold text-ink">
        <History size={16} strokeWidth={1.75} className="text-ink-3" aria-hidden />
        История изменений
        <span className="text-[12px] font-normal text-ink-3 tabular-nums">{sorted.length}</span>
      </h3>
      <p className="mt-0.5 mb-2 text-[12px] text-ink-3">
        Все табели человека за эту дату: здесь и в других бригадах.
      </p>
      {sorted.length === 0 ? (
        <p className="text-[13px] text-ink-3">Изменений по этому дню не было.</p>
      ) : (
        <ul className="space-y-1.5">
          {sorted.map((x) => (
            // Записи могут совпасть по времени и типу (пачка, отмена нескольких): ключ — и место.
            <li key={x.key} className="text-[12.5px] break-words text-ink-2">
              {x.text}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
